import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Suche } from '../common/Suche'
import { Sidebar } from './Sidebar'
import type { NavSection } from './Sidebar'
import { Topbar } from './Topbar'
import s from './AppShell.module.scss'

interface Props {
  navigation: NavSection[]
  /** Wer angemeldet ist. Wird unten in der Sidebar gezeigt. */
  user?: string
  onLogout?: () => void
  title: string
  subtitle?: string
  /** Rechts in der Kopfzeile: Chips, Schalter, was die Seite dort braucht. */
  actions?: ReactNode
  children: ReactNode
}

/**
 * Der Rahmen jeder Seite: Sidebar, Kopfzeile, Inhalt.
 *
 * Wie breit die Sidebar ist, entscheidet CSS und nicht JavaScript — es gibt
 * sonst zwei Wahrheiten ueber die Fensterbreite, und beim Drehen eines Tablets
 * laufen sie auseinander. Hier steht nur, OB die Sidebar aufgeklappt ist; was
 * das je Stufe bedeutet, steht in Sidebar.module.scss:
 *
 *   Desktop  ab 1280   feste Spalte, immer sichtbar, `open` ohne Wirkung
 *   Tablet   768-1279  schmaler Streifen, aufgeklappt als Ueberlagerung
 *   Mobile   unter 768 nicht sichtbar, aufgeklappt als Ueberlagerung
 */
export function AppShell({ navigation, user, onLogout, title, subtitle,
                          actions, children }: Props) {
  const [open, setOpen] = useState(false)
  const [suche, setSuche] = useState(false)

  // ⌘K auf dem Mac, Strg K sonst — von überall, auch aus einem Eingabefeld:
  // Mit Taste dazu ist die Absicht eindeutig.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSuche(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Solange die Sidebar ueberlagert, darf die Seite darunter nicht scrollen —
  // sonst wischt man am Handy die Liste weg, die man gerade nicht sieht.
  useEffect(() => {
    if (!open) return
    const vorher = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = vorher }
  }, [open])

  // Die Escape-Taste schliesst. Ohne sie ist die Ueberlagerung mit der Tastatur
  // allein nicht mehr zu verlassen.
  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <div className={s.shell}>
      <Sidebar
        sections={navigation}
        user={user}
        onLogout={onLogout}
        open={open}
        onClose={() => setOpen(false)}
      />

      {open && (
        <button
          type="button"
          className={s.backdrop}
          aria-label="Menü schließen"
          onClick={() => setOpen(false)}
        />
      )}

      <div className={s.main}>
        <Topbar
          title={title}
          subtitle={subtitle}
          actions={actions}
          onOpenNav={() => setOpen(true)}
          onSearch={() => setSuche(true)}
          user={user}
        />
        <main className={s.content}>{children}</main>
      </div>
      <Suche offen={suche} onClose={() => setSuche(false)} />
    </div>
  )
}
